// Shared renderer for the hand-written topic layers (/palworld-wiki/<slug> and
// /palworld-tcg/<slug>). Presentation only — every word comes from the page's
// own data entry, so no two URLs render the same copy.
import { Link } from "@tanstack/react-router";
import { FaqSection, type FaqItem } from "@/components/PageFaq";
import type { TopicPage } from "@/lib/topic-pages";

export function TopicArticle({
  page,
  eyebrow,
  parent,
  faqHeading,
}: {
  page: TopicPage;
  eyebrow: string;
  parent: { to: string; label: string };
  faqHeading: string;
}) {
  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-10">
      <p className="text-xs font-semibold uppercase tracking-wide text-primary">
        <Link to={parent.to} className="hover:underline">
          {eyebrow}
        </Link>
      </p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold">{page.h1}</h1>
      <p className="aeo-answer mt-4 text-muted-foreground">{page.answer}</p>

      {page.facts.length ? (
        <dl className="mt-6 grid gap-3 sm:grid-cols-2">
          {page.facts.map((f) => (
            <div key={f.label} className="glass rounded-xl p-4">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">{f.label}</dt>
              <dd className="mt-1 text-sm font-semibold">{f.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {page.table ? (
        <section className="mt-8">
          <h2 className="text-xl font-bold">{page.table.caption}</h2>
          <div className="mt-3 overflow-x-auto rounded-xl border border-border/60">
            <table className="w-full min-w-[520px] text-sm">
              <caption className="sr-only">{page.table.caption}</caption>
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  {page.table.columns.map((c) => (
                    <th key={c} scope="col" className="px-3 py-2 text-left font-semibold">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {page.table.rows.map((row) => (
                  <tr key={row.join("|")} className="border-t border-border/50">
                    {row.map((cell, i) => (
                      <td key={i} className={i === 0 ? "px-3 py-2 font-medium" : "px-3 py-2 text-muted-foreground"}>
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <div className="mt-10 space-y-8">
        {page.sections.map((s) => (
          <section key={s.heading}>
            <h2 className="text-xl font-bold">{s.heading}</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
            {s.bullets?.length ? (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : null}
          </section>
        ))}
      </div>

      <section className="mt-10">
        <h2 className="text-xl font-bold">Where to go next</h2>
        <ul className="mt-4 grid gap-3 sm:grid-cols-2">
          {page.links.map((l) => (
            <li key={l.to} className="rounded-lg border border-border/60 p-3">
              <Link to={l.to} className="text-sm font-semibold text-primary hover:underline">
                {l.label}
              </Link>
              <p className="mt-1 text-xs text-muted-foreground">{l.note}</p>
            </li>
          ))}
        </ul>
      </section>

      <FaqSection items={page.faq as FaqItem[]} heading={faqHeading} />
    </div>
  );
}
